import { useCallback, useState } from 'react';
import { IProcountorInvoicePreviewResponse } from '../ZentuvoInvoicingApi';
import { collectDefaultSelectedRowIds, keepExistingSelection, normalizeOrderRowId } from '../procountorInvoiceHelpers';

type UseInvoiceSelectionResult = {
    selectedRowIds: number[];
    clearSelection: () => void;

    applyFromResponse: (response: IProcountorInvoicePreviewResponse, preserveSelection: boolean) => void;
    toggleRowSelection: (orderRowId: number | string) => void;
    toggleMultipleRows: (orderRowIds: Array<number | string>) => void;
};

export function useInvoiceSelection(): UseInvoiceSelectionResult {
    const [selectedRowIds, setSelectedRowIds] = useState<number[]>([]);

    const clearSelection = useCallback(() => setSelectedRowIds([]), []);

    const applyFromResponse = useCallback((response: IProcountorInvoicePreviewResponse, preserveSelection: boolean) => {
        setSelectedRowIds((previouslySelectedRowIds) => {
            if (preserveSelection) {
                return keepExistingSelection(response, previouslySelectedRowIds);
            }

            return collectDefaultSelectedRowIds(response);
        });
    }, []);

    const toggleRowSelection = useCallback((orderRowId: number | string) => {
        const normalizedOrderRowId = normalizeOrderRowId(orderRowId);
        setSelectedRowIds((currentSelectedRowIds) => {
            if (currentSelectedRowIds.indexOf(normalizedOrderRowId) >= 0) {
                return currentSelectedRowIds.filter((value) => value !== normalizedOrderRowId);
            }

            return currentSelectedRowIds.concat(normalizedOrderRowId);
        });
    }, []);

    const toggleMultipleRows = useCallback((orderRowIds: Array<number | string>) => {
        const normalizedOrderRowIds = orderRowIds
            .map((orderRowId) => normalizeOrderRowId(orderRowId))
            .filter((orderRowId) => orderRowId >= 0);

        setSelectedRowIds((currentSelectedRowIds) => {
            const everyRowSelected = normalizedOrderRowIds.every((orderRowId) => currentSelectedRowIds.indexOf(orderRowId) >= 0);
            if (everyRowSelected) {
                return currentSelectedRowIds.filter((orderRowId) => normalizedOrderRowIds.indexOf(orderRowId) === -1);
            }

            const nextSelectedRowIds = currentSelectedRowIds.slice();
            normalizedOrderRowIds.forEach((orderRowId) => {
                if (nextSelectedRowIds.indexOf(orderRowId) === -1) {
                    nextSelectedRowIds.push(orderRowId);
                }
            });
            return nextSelectedRowIds;
        });
    }, []);

    return {
        selectedRowIds,
        clearSelection,
        applyFromResponse,
        toggleRowSelection,
        toggleMultipleRows
    };
}
